import React, { useState } from 'react'
import styled from 'styled-components'
import Modal, { ModalHeader, ModalBody, ModalFooter } from 'core-system/Modal'
import Text from 'core-system/Text'
import Radio from 'core-system/Radio'
import { useDispatch } from 'react-redux'
import { employeePlatformActions } from 'redux/employeePlatform/employeePlatformSlice'
import AlertIcon from 'core-system/Icons/Misc/Alert'
import pxToRem from 'core-system/utils/pxToRem'
import palette from 'core-system/Themes/palette'
import { GRHCopy, GRHReasons } from '../EmployeeGRHUtils'
import { ContentState, RawDraftContentState, convertToRaw } from 'draft-js'
import TextEditor from 'core-system/TextEditor'
import { rawDraftToString } from 'employee-platform/shared/utils'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import { useNavigate } from 'react-router-dom'
import { Locations } from 'shared/Router/Locations'

const GRHDisclaimerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  border-radius: 0.5rem;
  background-color: ${(props) => props.theme.palette.secondary.purple200};
`

const GRHDisclaimerHeader = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
`

const ReasonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
`

interface GRHRequestModalProps {
  open: boolean
  closeModal: () => void
  programId: string | null
}

const counties = ['Alameda', 'San Francisco']
const grhTypes = ['Employer', 'Government']

const GRHRequestModal = React.memo((props: GRHRequestModalProps) => {
  const navigate = useNavigate()
  const { open, closeModal, programId } = props

  const dispatch = useDispatch()

  const [selectedReason, setSelectedReason] = useState(null)
  const [selectedCounty, setSelectedCounty] = useState(counties[0])
  const [otherReason, setOtherReason] = useState<RawDraftContentState>(
    convertToRaw(ContentState.createFromText(''))
  )
  const [selectedGrhType, setSelectedGrhType] = useState(grhTypes[0])

  const handleCloseModal = () => {
    closeModal()
  }

  const handleRequestGRH = () => {
    dispatch(
      employeePlatformActions.createProgramRequest({
        programId: programId,
        reason:
          selectedReason === 'Other'
            ? `${selectedReason} - ${rawDraftToString(otherReason)}`
            : selectedReason,
      })
    )
    handleCloseModal()
  }

  const submitButtonObject = () => {
    const buttonObject = {
      text: 'Submit Request',
      onClick: handleRequestGRH,
    }
    if (selectedGrhType === 'Employer') {
      return buttonObject
    }
    if (selectedGrhType === 'Government') {
      buttonObject.text = 'Next Step'
      buttonObject.onClick = () =>
        navigate(Locations.EmployeeGRH.Status, {
          state: {
            selectedCounty: selectedCounty,
          },
        })
    }
    return buttonObject
  }

  return (
    <Modal
      minWidth={pxToRem(600)}
      open={open}
      onClose={() => handleCloseModal()}
    >
      <ModalHeader title='Request GRH' />
      <ModalBody>
        {selectedGrhType === 'Employer' && (
          <GRHDisclaimerContainer>
            <GRHDisclaimerHeader>
              <AlertIcon
                height={pxToRem(20)}
                width={pxToRem(20)}
                color={palette.secondary.purple600}
              />
              <Text variant='action3' textColor={palette.secondary.purple600}>
                REMINDER
              </Text>
            </GRHDisclaimerHeader>
            <Text variant='action4'>{GRHCopy.reminder}</Text>
          </GRHDisclaimerContainer>
        )}
        {/* Continue to develop the government grh program on the web later */}
        {/* <Text
          variant='action3'
          textColor={palette.text.secondary}
          marginTop='1rem'
        >
          GRH Request Type:
        </Text>
        {grhTypes.map((grhType) => {
          return (
            <ReasonContainer key={grhType}>
              <Radio
                active={selectedGrhType === grhType}
                onClick={() => setSelectedGrhType(grhType)}
              />
              <Text variant='action4'>{grhType}</Text>
            </ReasonContainer>
          )
        })} */}
        {selectedGrhType === 'Government' && (
          <>
            <Text
              variant='action3'
              textColor={palette.text.secondary}
              marginTop='1rem'
            >
              Select County:
            </Text>
            {counties.map((county) => {
              return (
                <ReasonContainer key={county}>
                  <Radio
                    active={selectedCounty === county}
                    onClick={() => setSelectedCounty(county)}
                  />
                  <Text variant='action4'>{county}</Text>
                </ReasonContainer>
              )
            })}
          </>
        )}
        <Text
          variant='action3'
          textColor={palette.text.secondary}
          marginTop='1rem'
        >
          Select a Reason:
        </Text>
        {GRHReasons.map((reason) => {
          return (
            <ReasonContainer key={reason}>
              <Radio
                active={selectedReason === reason}
                onClick={() => setSelectedReason(reason)}
              />
              <Text variant='action4'>{reason}</Text>
            </ReasonContainer>
          )
        })}
        {selectedReason === 'Other' ? (
          <TextEditor
            label='Please specify:'
            contentState={otherReason}
            oncontentStateChange={(contentState) =>
              setOtherReason(contentState)
            }
            hideToolbar
            marginTop='0.5rem'
          />
        ) : null}
      </ModalBody>
      <ModalFooter>
        <SecondaryButton onClick={() => handleCloseModal()}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          disabled={
            !selectedReason ||
            !programId ||
            (selectedReason === 'Other' && rawDraftToString(otherReason) === '')
          }
          onClick={() => submitButtonObject().onClick()}
          style={{ marginLeft: '1rem' }}
        >
          {submitButtonObject().text}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  GRHRequestModal.displayName = 'GRHRequestModal'
}

export default GRHRequestModal
